import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  ClickAwayListener,
  Collapse,
  Popper,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Chip,
  Stack,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Insights as InsightsIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import {
  ConversationArchiveStatus,
  DeclineReason,
  ConversationFilters,
  ConversationLossReasonFilter,
} from '../../services/conversationService';
import ConversationFunnelCard from './ConversationFunnelCard';
import {
  conversationLeadStatusDefinitions,
  conversationLossReasonDefinitions,
  ConversationLeadStatus,
} from './conversationLeadStatus';

interface ConversationFiltersProps {
  filters: ConversationFilters;
  searchQuery: string;
  selectedLeadStatus?: ConversationLeadStatus;
  selectedLossReason?: ConversationLossReasonFilter;
  selectedDeclineReason?: DeclineReason;
  refreshTrigger?: number;
  onFiltersChange: (filters: ConversationFilters) => void;
  onSearchChange: (query: string) => void;
  onClearFilters: () => void;
  onLeadStatusSelect: (status?: ConversationLeadStatus) => void;
  onLossReasonSelect: (lossReason?: ConversationLossReasonFilter) => void;
  onDeclineReasonSelect: (declineReason?: DeclineReason) => void;
}

const projectOptions = [
  'Acuarela del Parque',
  'Allegra Apartamentos',
  'Alto Jardín',
  'Amonte',
  'Artcity',
  'Bosque Alto',
  'Ciudad del Bosque',
  'DC Home',
  'Green Park',
  'Kristal Blu',
  'Life 72',
  'Luna Apartamentos',
  'Mint Natura',
  'Parc Avignon',
  'Q Tower',
  'Saint Michel',
  'Tribeca',
  'Urban Apartaestudios',
  'Village',
  'Vivace Apartamentos',
];

const ConversationFiltersComponent: React.FC<ConversationFiltersProps> = ({
  filters,
  searchQuery,
  selectedLeadStatus,
  selectedLossReason,
  selectedDeclineReason,
  refreshTrigger,
  onFiltersChange,
  onSearchChange,
  onClearFilters,
  onLeadStatusSelect,
  onLossReasonSelect,
  onDeclineReasonSelect,
}) => {
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showFunnel, setShowFunnel] = useState(false);
  const funnelAnchorRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  // Debounced search
  useEffect(() => {
    if (searchInput === searchQuery) {
      return undefined;
    }

    const timer = setTimeout(() => {
      onSearchChange(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, searchQuery, onSearchChange]);

  const sanitizeFilters = (nextFilters: ConversationFilters): ConversationFilters => {
    const sanitizedFilters = { ...nextFilters };

    Object.keys(sanitizedFilters).forEach((key) => {
      if (sanitizedFilters[key as keyof ConversationFilters] === undefined) {
        delete sanitizedFilters[key as keyof ConversationFilters];
      }
    });

    return sanitizedFilters;
  };

  const handleFilterChange = (field: keyof ConversationFilters, value: any) => {
    const nextFilters: ConversationFilters = {
      ...filters,
      [field]: value || undefined,
    };

    if (field === 'lead_status') {
      nextFilters.lead_statuses = undefined;
      nextFilters.loss_reason = undefined;
      nextFilters.decline_reason = undefined;
    }

    onFiltersChange(
      sanitizeFilters(nextFilters)
    );
  };

  const handleClearFilters = () => {
    setSearchInput('');
    onClearFilters();
  };

  const getActiveFiltersCount = (): number => {
    let count = 0;
    if (searchInput.trim()) count++;
    if (filters.lead_status || filters.lead_statuses?.length) count++;
    if (filters.loss_reason) count++;
    if (filters.decline_reason) count++;
    if (filters.project_interest) count++;
    if (filters.customer_phone) count++;
    if (filters.start_date) count++;
    if (filters.end_date) count++;
    if (filters.is_active !== undefined) count++;
    if (filters.archive_status && filters.archive_status !== 'active') count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();
  const selectedFunnelStatusLabel = selectedLeadStatus
    ? conversationLeadStatusDefinitions.find(
        (statusDefinition) => statusDefinition.value === selectedLeadStatus
      )?.label
    : undefined;
  const selectedLossReasonLabel = filters.loss_reason
    ? conversationLossReasonDefinitions.find(
        (reasonDefinition) => reasonDefinition.value === filters.loss_reason
      )?.label
    : undefined;

  return (
    <Paper sx={{ p: 3, height: '100%' }}>
        <Grid container spacing={3}>
          {/* Search Bar */}
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre, teléfono, contenido de mensajes..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchInput('')}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Filter Toggle */}
          <Grid item xs={12} md={4}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              justifyContent="flex-end"
              height="100%"
              flexWrap="wrap"
            >
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                endIcon={showAdvancedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                sx={{ minWidth: 140 }}
              >
                Filtros
                {activeFiltersCount > 0 && (
                  <Chip
                    label={activeFiltersCount}
                    size="small"
                    color="primary"
                    sx={{ ml: 1, height: 20, minWidth: 20 }}
                  />
                )}
              </Button>

              <Button
                ref={funnelAnchorRef}
                variant={showFunnel || selectedLeadStatus || selectedLossReason || selectedDeclineReason ? 'contained' : 'outlined'}
                startIcon={<InsightsIcon />}
                onClick={() => setShowFunnel((previousValue) => !previousValue)}
                aria-expanded={showFunnel}
                aria-haspopup="dialog"
              >
                Embudo
              </Button>

              {activeFiltersCount > 0 && (
                <Button
                  variant="text"
                  color="secondary"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                >
                  Limpiar
                </Button>
              )}
            </Stack>

            <Popper
              open={showFunnel}
              anchorEl={funnelAnchorRef.current}
              placement="bottom-end"
              style={{ zIndex: 1600 }}
            >
              <ClickAwayListener onClickAway={() => setShowFunnel(false)}>
                <Paper
                  elevation={8}
                  sx={{
                    mt: 1,
                    p: 2.5,
                    width: { xs: 'min(92vw, 560px)', sm: 560 },
                    maxWidth: '92vw',
                    borderRadius: 3,
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <ConversationFunnelCard
                    selectedStatus={selectedLeadStatus}
                    selectedLossReason={selectedLossReason}
                    selectedDeclineReason={selectedDeclineReason}
                    onStatusSelect={onLeadStatusSelect}
                    onLossReasonSelect={onLossReasonSelect}
                    onDeclineReasonSelect={onDeclineReasonSelect}
                    refreshTrigger={refreshTrigger}
                  />
                </Paper>
              </ClickAwayListener>
            </Popper>
          </Grid>
        </Grid>

        {/* Advanced Filters */}
        <Collapse in={showAdvancedFilters}>
          <Box mt={3}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Filtros Avanzados
            </Typography>

            <Grid container spacing={3}>
              {/* Lead Status */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estado del Lead</InputLabel>
                  <Select
                    value={filters.lead_status || ''}
                    label="Estado del Lead"
                    onChange={(e) => handleFilterChange('lead_status', e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {conversationLeadStatusDefinitions.map((statusDefinition) => (
                      <MenuItem key={statusDefinition.value} value={statusDefinition.value}>
                        {statusDefinition.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Project Interest */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Proyecto de Interés</InputLabel>
                  <Select
                    value={filters.project_interest || ''}
                    label="Proyecto de Interés"
                    onChange={(e) => handleFilterChange('project_interest', e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {projectOptions.map((project) => (
                      <MenuItem key={project} value={project}>
                        {project}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Customer Phone */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Teléfono del Cliente"
                  value={filters.customer_phone || ''}
                  onChange={(e) => handleFilterChange('customer_phone', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Active Status */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estado de Conversación</InputLabel>
                  <Select
                    value={filters.is_active === undefined ? '' : filters.is_active.toString()}
                    label="Estado de Conversación"
                    onChange={(e) => {
                      const value = e.target.value;
                      handleFilterChange('is_active', value === '' ? undefined : value === 'true');
                    }}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="true">Activas</MenuItem>
                    <MenuItem value="false">Finalizadas</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Archivado</InputLabel>
                  <Select
                    value={(filters.archive_status || 'active') as ConversationArchiveStatus}
                    label="Archivado"
                    onChange={(e) => handleFilterChange('archive_status', e.target.value)}
                  >
                    <MenuItem value="active">Solo activas</MenuItem>
                    <MenuItem value="archived">Solo archivadas</MenuItem>
                    <MenuItem value="all">Todas</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Date Range */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Fecha de Inicio"
                  type="date"
                  value={filters.start_date ? filters.start_date.split('T')[0] : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFilterChange('start_date', value ? new Date(value).toISOString() : undefined);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Fecha de Fin"
                  type="date"
                  value={filters.end_date ? filters.end_date.split('T')[0] : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFilterChange('end_date', value ? new Date(value).toISOString() : undefined);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

            </Grid>

            {/* Active Filters Display */}
            {activeFiltersCount > 0 && (
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Filtros activos:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {searchInput.trim() && (
                    <Chip
                      label={`Búsqueda: "${searchInput}"`}
                      size="small"
                      onDelete={() => setSearchInput('')}
                    />
                  )}
                  {filters.lead_status && (
                    <Chip
                      label={`Estado: ${conversationLeadStatusDefinitions.find(
                        (statusDefinition) => statusDefinition.value === filters.lead_status
                      )?.label}`}
                      size="small"
                      onDelete={() => handleFilterChange('lead_status', undefined)}
                    />
                  )}
                  {selectedLeadStatus && filters.lead_statuses?.length ? (
                    <Chip
                      label={`Embudo: ${selectedFunnelStatusLabel}${
                        selectedLeadStatus === 'lost' || filters.loss_reason ? '' : '+'
                      }`}
                      size="small"
                      onDelete={() => onLeadStatusSelect(undefined)}
                    />
                  ) : null}
                  {filters.loss_reason && (
                    <Chip
                      label={`Pérdida: ${selectedLossReasonLabel || filters.loss_reason}`}
                      size="small"
                      onDelete={() => onLossReasonSelect(undefined)}
                    />
                  )}
                  {filters.decline_reason && (
                    <Chip
                      label={`Causa: ${filters.decline_reason}`}
                      size="small"
                      onDelete={() => onDeclineReasonSelect(undefined)}
                    />
                  )}
                  {filters.project_interest && (
                    <Chip
                      label={`Proyecto: ${filters.project_interest}`}
                      size="small"
                      onDelete={() => handleFilterChange('project_interest', undefined)}
                    />
                  )}
                  {filters.customer_phone && (
                    <Chip
                      label={`Teléfono: ${filters.customer_phone}`}
                      size="small"
                      onDelete={() => handleFilterChange('customer_phone', undefined)}
                    />
                  )}
                  {filters.start_date && (
                    <Chip
                      label={`Desde: ${format(new Date(filters.start_date), 'dd/MM/yyyy')}`}
                      size="small"
                      onDelete={() => handleFilterChange('start_date', undefined)}
                    />
                  )}
                  {filters.end_date && (
                    <Chip
                      label={`Hasta: ${format(new Date(filters.end_date), 'dd/MM/yyyy')}`}
                      size="small"
                      onDelete={() => handleFilterChange('end_date', undefined)}
                    />
                  )}
                  {filters.is_active !== undefined && (
                    <Chip
                      label={`Estado: ${filters.is_active ? 'Activas' : 'Finalizadas'}`}
                      size="small"
                      onDelete={() => handleFilterChange('is_active', undefined)}
                    />
                  )}
                  {filters.archive_status && filters.archive_status !== 'active' && (
                    <Chip
                      label={`Archivado: ${
                        filters.archive_status === 'archived' ? 'Solo archivadas' : 'Todas'
                      }`}
                      size="small"
                      onDelete={() => handleFilterChange('archive_status', 'active')}
                    />
                  )}
                </Stack>
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>
  );
};

export default ConversationFiltersComponent;
